/*
PWA install prompt — bottom-pinned banner that surfaces the install
flow.

Two distinct platforms, two distinct paths:

- **Chromium** (Android Chrome / desktop Chrome / Edge / Brave) fires
  `beforeinstallprompt` whenever the page becomes installable. The
  spec lets us preventDefault + stash the event, then call its
  `prompt()` later from a user gesture. We do exactly that: capture
  the event, surface our banner, and on tap call `prompt()` so the
  native dialog opens. After the user accepts or dismisses, the
  event is consumed and our banner closes.

- **iOS Safari** has no programmatic install API. The only way is
  the user manually picking "Add to Home Screen" from the Share
  sheet. The contextual hint we surface ("Partager → Sur l'écran
  d'accueil") at least closes the discoverability gap. We detect
  iOS via UA + standalone heuristic and skip the hint when the page
  is already running standalone (the user already installed).

Dismissal is sticky per-browser: once the user closes the banner we
write a flag to localStorage so it doesn't pester. The flag is
cleared automatically when the page detects it's running in
standalone mode (the user took us up on the offer — keep the flag
out of the way for the next reinstall scenario).
*/

const kDismissKey: string = 'canopaname.install.dismissed'  // localStorage key, set after the user closes the banner
const kBannerId: string = 'install-prompt-banner'           // single global element id, used to dedupe re-injections
const kRevealDelayMs: number = 3500                          // small delay so the banner doesn't slam the very-first paint

/*
Subset of the BeforeInstallPromptEvent interface we actually use.
The DOM lib doesn't ship a typed declaration for it (it's a Chromium
extension to the spec) so we hand-roll a minimal one.
*/
interface BeforeInstallPromptEvent extends Event
{
	prompt(): Promise<void>
	userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

let fInstalled: boolean = false                                              // module-level guard so installSubsystem only runs once
let fDeferredEvent: BeforeInstallPromptEvent | null = null                   // captured beforeinstallprompt, used when the user taps "Installer"

/*
Wire the platform listeners. Idempotent; safe to call from main.ts
before the router mounts. The banner itself is lazily injected when
either the deferred event arrives (Chromium) or the iOS heuristic
matches.
*/
export function installInstallPrompt(): void
{
	if (fInstalled) return
	fInstalled = true
	if (typeof window === 'undefined') return

	// Chromium path — capture the event before the browser shows its
	// own mini-infobar so we can decide when to surface our banner.
	window.addEventListener('beforeinstallprompt', (inEv) => {
		const vEvent = inEv as BeforeInstallPromptEvent
		vEvent.preventDefault()
		fDeferredEvent = vEvent
		if (isStandalone()) return              // already installed — nothing to offer
		if (isDismissed()) return               // user said "no thanks" earlier in this profile
		window.setTimeout(() => showBanner('chromium'), kRevealDelayMs)
	})

	// If the user installs from the browser chrome (not via our
	// banner), Chrome fires `appinstalled`. Drop our deferred state
	// and clear the dismissal flag so a future re-install scenario
	// starts from a clean slate.
	window.addEventListener('appinstalled', () => {
		fDeferredEvent = null
		try { localStorage.removeItem(kDismissKey) } catch { /* private mode */ }
		hideBanner()
	})

	// iOS Safari path — no event, just heuristics. Show the
	// contextual hint once unless the user has dismissed it.
	if (isIosSafariNonStandalone() && !isDismissed())
	{
		window.setTimeout(() => showBanner('ios'), kRevealDelayMs)
	}
}

/*
Inject (or re-show) the banner. The DOM is recreated each time so
the per-platform copy + behaviour stay aligned with the trigger.
*/
function showBanner(inPlatform: 'chromium' | 'ios'): void
{
	if (isStandalone()) return  // last-ditch guard — display-mode flips between trigger and reveal
	const vExisting = document.getElementById(kBannerId)
	if (vExisting) vExisting.remove()

	const vBanner = document.createElement('div')
	vBanner.id = kBannerId
	vBanner.className = 'install-prompt'
	vBanner.setAttribute('role', 'status')
	vBanner.setAttribute('data-platform', inPlatform)
	vBanner.setAttribute('data-open', 'false')
	vBanner.innerHTML = inPlatform === 'chromium'
		? renderChromiumMarkup()
		: renderIosMarkup()
	document.body.appendChild(vBanner)
	requestAnimationFrame(() => vBanner.setAttribute('data-open', 'true'))

	// Dismiss handler — same code path for both platforms.
	const vCloseBtn = vBanner.querySelector<HTMLButtonElement>('.install-prompt-close')
	vCloseBtn?.addEventListener('click', () => {
		try { localStorage.setItem(kDismissKey, '1') } catch { /* private mode */ }
		hideBanner()
	})

	if (inPlatform === 'chromium')
	{
		const vInstall = vBanner.querySelector<HTMLButtonElement>('.install-prompt-action')
		vInstall?.addEventListener('click', async () => {
			if (!fDeferredEvent) { hideBanner(); return }
			try
			{
				await fDeferredEvent.prompt()
				const vChoice = await fDeferredEvent.userChoice
				if (vChoice.outcome === 'dismissed')
				{
					// Treat browser-level "not now" as a soft dismiss so we
					// don't immediately re-prompt — but DON'T set the
					// permanent flag (the user might still want to install
					// later, the browser will re-fire beforeinstallprompt).
				}
			}
			catch
			{
				// prompt() throws "InvalidStateError" if already consumed —
				// safe to ignore, the banner will hide either way.
			}
			fDeferredEvent = null
			hideBanner()
		})
	}
}

function hideBanner(): void
{
	const vBanner = document.getElementById(kBannerId)
	if (!vBanner) return
	vBanner.setAttribute('data-open', 'false')
	window.setTimeout(() => vBanner.remove(), 220)
}

function renderChromiumMarkup(): string
{
	return `
		<span class="install-prompt-icon" aria-hidden="true">⤓</span>
		<span class="install-prompt-text">Installer CanoPaname pour y accéder hors ligne, sans la barre du navigateur.</span>
		<button class="install-prompt-action" type="button">Installer</button>
		<button class="install-prompt-close" type="button" aria-label="Plus tard">×</button>
	`
}

function renderIosMarkup(): string
{
	return `
		<span class="install-prompt-icon" aria-hidden="true">⤓</span>
		<span class="install-prompt-text">
			Pour installer CanoPaname&nbsp;: <strong>Partager</strong> <span aria-hidden="true">↑</span> →
			<strong>Sur l'écran d'accueil</strong>.
		</span>
		<button class="install-prompt-close" type="button" aria-label="Compris">×</button>
	`
}

/*
True when the page runs as an installed PWA (Chromium / Edge: the
display-mode media query; iOS Safari: navigator.standalone). Either
signal flips the banner off — it would be absurd to nag the user to
install something they're already running standalone.
*/
function isStandalone(): boolean
{
	try
	{
		if (window.matchMedia('(display-mode: standalone)').matches) return true
	}
	catch { /* matchMedia missing on very old browsers — treat as not-standalone */ }
	const vNav = navigator as Navigator & { standalone?: boolean }
	return vNav.standalone === true
}

/*
Heuristic for Safari iOS in a non-standalone tab. iPad on iOS 13+
masquerades as macOS in the UA, so we also check for touch + Mac
("iPadOS" detection) to catch the modern iPad case.
*/
function isIosSafariNonStandalone(): boolean
{
	if (isStandalone()) return false
	if (typeof navigator === 'undefined') return false
	const vUa = navigator.userAgent
	const vIsClassicIos = /iPad|iPhone|iPod/.test(vUa)
	const vIsModernIpad = /Mac/.test(vUa) && typeof (navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints === 'number' && (navigator as Navigator & { maxTouchPoints?: number }).maxTouchPoints! > 1
	if (!vIsClassicIos && !vIsModernIpad) return false
	// On iOS, only Safari supports Add-to-Home-Screen. Other in-app
	// browsers (Chrome iOS, Firefox iOS) ride the same WebKit but the
	// share-sheet path doesn't expose the option — surfacing our hint
	// there would be misleading. We sniff for Safari brand string.
	const vIsCriOs = /CriOS|FxiOS|EdgiOS/.test(vUa)
	if (vIsCriOs) return false
	return /Safari/.test(vUa)
}

function isDismissed(): boolean
{
	try { return localStorage.getItem(kDismissKey) === '1' }
	catch { return false }
}
